import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  InputBase,
  Link as MuiLink,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { Seo } from '../components/seo/Seo';
import { caliberToSlug } from '../utils/caliber';
import { buildCanonicalUrl } from '../utils/seo/canonical';
import { websiteWithSearch, organization } from '../utils/seo/jsonLd';
import {
  searchListings,
  getDeals,
  getTaxonomy,
  getCaliberInventoryCounts,
  type DealsResponse,
  type FacetValue,
  type SearchHit,
  type SearchResponse,
  type TaxonomyTerm,
} from '../services/searchApi';
import { ListingCard } from '../components/search/ListingCard';
import { Eyebrow } from '../components/brand/Eyebrow';
import { Cartridge } from '../components/brand/Cartridge';

// ── Constants ──────────────────────────────────────────────────────────────

/** Fallback caliber list (spec §15.2) — keeps the page navigable on a fresh DB. */
const FALLBACK_CALIBERS: string[] = [
  '9mm Luger',
  '.223 Remington',
  '5.56 NATO',
  '.45 ACP',
  '.308 Winchester',
  '12 Gauge',
  '.22 LR',
  '.300 Blackout',
];

const TOP_CALIBER_COUNT = 8;
const DEALS_LIMIT = 8;
const BACK_IN_STOCK_LIMIT = 8;
const BROWSE_CALIBER_COUNT = 12;

const TRY_SUGGESTIONS: Array<{ label: string; to: string }> = [
  { label: '5.56 bulk', to: '/search?caliber=5.56%20NATO&sort=pricePerRound&sortDir=asc' },
  { label: '.308 match', to: '/search?caliber=.308%20Winchester&bulletType=Match' },
  { label: 'cheapest .22lr', to: '/search?caliber=.22%20LR&sort=pricePerRound&sortDir=asc' },
  { label: '300blk subsonic', to: '/search?caliber=.300%20Blackout' },
];

// ── Top-level page ─────────────────────────────────────────────────────────

// #103: advertiser-facing entry points are premature until we have traffic to
// pitch. Keep the section + route; flip this to true to surface it again.
const SHOW_ADVERTISER_CTA: boolean = false;

export function HomePage() {
  return (
    <Stack spacing={{ xs: 6, md: 9 }} component="div" sx={{ pb: 4 }}>
      <Seo
        title="Find In-Stock Ammunition by Caliber, Brand, and Price"
        description="Round Recon aggregates ammo listings from many retailers, ranks by price-per-round and freshness, and notifies you when your criteria are met."
        canonical={buildCanonicalUrl('/')}
        jsonLd={[websiteWithSearch(), organization()]}
      />
      <HeroSection />
      <CaliberShortcutsSection />
      <BestDealsSection />
      <BrowseByCaliberSection />
      <BackInStockSection />
      <PriceDropCtaSection />
      <PrivacyNoticeSection />
      {/* Temporarily hidden until we have enough traffic to pitch advertisers
          (#103). The section component is kept below — flip the flag to restore. */}
      {SHOW_ADVERTISER_CTA && <AdvertiserCtaSection />}
    </Stack>
  );
}

export default HomePage;

// ── Shared tactical search bar ──────────────────────────────────────────────

function SearchBar({ size = 'lg' }: { size?: 'lg' | 'sm' }) {
  const theme = useTheme();
  const t = theme.palette.tactical;
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const large = size === 'lg';

  function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmed = query.trim();
    if (trimmed.length === 0) return;
    navigate(`/search?q=${encodeURIComponent(trimmed)}`);
  }

  return (
    <Box
      component="form"
      role="search"
      aria-label="Ammunition search"
      onSubmit={onSubmit}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        bgcolor: t.panel,
        border: `1px solid ${t.lineHi}`,
        borderRadius: 3,
        p: 0.75,
        boxShadow: `0 24px 48px -16px rgba(0,0,0,0.6)`,
        transition: 'border-color 120ms, background-color 120ms, box-shadow 120ms',
        // #104: the input only shows a caret on focus. Give the whole field a
        // prominent focus indicator — accent border, lighter fill, and a ring.
        '&:focus-within': {
          bgcolor: t.panelHi,
          borderColor: t.accent,
          boxShadow: `0 24px 48px -16px rgba(0,0,0,0.6), 0 0 0 3px ${t.accent}55`,
        },
      }}
    >
      <SearchIcon sx={{ ml: 1, color: t.dim, fontSize: large ? 22 : 18 }} aria-hidden="true" />
      <InputBase
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="e.g. 9mm 124gr HST — cheapest delivered"
        inputProps={{ 'aria-label': 'Search ammunition' }}
        sx={{ flex: 1, color: t.text, fontSize: large ? '1.05rem' : '0.9rem', px: 0.5 }}
      />
      <Button type="submit" variant="contained" color="primary" size={large ? 'large' : 'medium'}>
        Search
      </Button>
    </Box>
  );
}

// ── 1. Hero ──────────────────────────────────────────────────────────────────

function useMarketSummary() {
  return useQuery<SearchResponse>({
    queryKey: ['search', 'market-summary'],
    queryFn: () => searchListings({ inStockOnly: true, limit: 0 }),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

function HeroSection() {
  const theme = useTheme();
  const t = theme.palette.tactical;
  const summary = useMarketSummary();

  const listings = summary.data?.totalResults ?? null;
  const calibers = summary.data?.facets?.caliber?.length ?? null;
  const retailers = summary.data?.facets?.retailerName?.length ?? null;

  const stats: Array<{ value: string; label: string; sub: string }> = [];
  if (listings != null)
    stats.push({
      value: listings.toLocaleString(),
      label: 'in-stock listings',
      sub: 'across the live market',
    });
  if (calibers != null)
    stats.push({ value: String(calibers), label: 'calibers', sub: 'indexed right now' });
  if (retailers != null)
    stats.push({ value: String(retailers), label: 'retailers', sub: 'compared every scrape' });

  return (
    <Box
      component="section"
      aria-labelledby="hero-heading"
      sx={{ position: 'relative', textAlign: 'center', pt: { xs: 4, md: 7 } }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `linear-gradient(${t.line} 1px, transparent 1px), linear-gradient(90deg, ${t.line} 1px, transparent 1px)`,
          backgroundSize: '48px 48px',
          maskImage: 'radial-gradient(ellipse at center, rgba(0,0,0,0.5) 0%, transparent 70%)',
          opacity: 0.5,
          pointerEvents: 'none',
        }}
      />
      <Box sx={{ position: 'relative' }}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            px: 1.25,
            py: 0.5,
            mb: 3,
            border: `1px solid ${t.line}`,
            bgcolor: t.panel,
            borderRadius: 999,
            fontFamily: t.monoFont,
            fontSize: 11,
            color: t.dim,
          }}
        >
          <Box
            component="span"
            sx={{
              bgcolor: t.accent,
              color: t.accentInk,
              fontWeight: 700,
              px: 1,
              py: 0.25,
              borderRadius: 999,
              letterSpacing: '0.06em',
            }}
          >
            LIVE
          </Box>
          Real-time prices, ranked by total delivered cost
        </Box>

        <Typography
          id="hero-heading"
          variant="h1"
          component="h1"
          sx={{
            fontSize: { xs: '2.4rem', sm: '3.4rem', md: '4.5rem' },
            lineHeight: 1.02,
            letterSpacing: '-0.035em',
            maxWidth: 900,
            mx: 'auto',
          }}
        >
          The fastest way to{' '}
          <Box
            component="span"
            sx={{ bgcolor: t.accent, color: t.accentInk, px: 1, borderRadius: 1.5 }}
          >
            find ammunition
          </Box>{' '}
          on the internet.
        </Typography>

        <Typography
          variant="body1"
          sx={{
            color: t.dim,
            maxWidth: 680,
            mx: 'auto',
            mt: 3,
            fontSize: '1.05rem',
            lineHeight: 1.5,
          }}
        >
          Round Recon scans many retailers in real time and ranks every result by total delivered
          cost — so you stop overpaying for the same box of brass.
        </Typography>

        <Box sx={{ maxWidth: 760, mx: 'auto', mt: 4 }}>
          <SearchBar size="lg" />
          <Stack
            direction="row"
            spacing={2}
            justifyContent="center"
            flexWrap="wrap"
            useFlexGap
            sx={{ mt: 2.25, fontFamily: t.monoFont, fontSize: 12, color: t.dim }}
          >
            <Box component="span">try:</Box>
            {TRY_SUGGESTIONS.map((s) => (
              <MuiLink
                key={s.label}
                component={RouterLink}
                to={s.to}
                underline="hover"
                sx={{ color: t.dim, '&:hover': { color: t.text } }}
              >
                {s.label}
              </MuiLink>
            ))}
          </Stack>
        </Box>

        {stats.length > 0 && (
          <Box
            sx={{
              mt: { xs: 5, md: 8 },
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: `repeat(${stats.length}, 1fr)` },
              maxWidth: 760,
              mx: 'auto',
              borderTop: `1px solid ${t.line}`,
              borderBottom: `1px solid ${t.line}`,
            }}
          >
            {stats.map((s, i) => (
              <Box
                key={s.label}
                sx={{
                  px: 3,
                  py: 3.5,
                  textAlign: 'left',
                  borderRight: { sm: i < stats.length - 1 ? `1px solid ${t.line}` : 'none' },
                }}
              >
                <Typography
                  component="div"
                  sx={{
                    fontSize: '2.2rem',
                    fontWeight: 600,
                    letterSpacing: '-0.02em',
                    lineHeight: 1,
                  }}
                >
                  {s.value}
                </Typography>
                <Typography component="div" sx={{ fontSize: 13, color: t.text, mt: 0.75 }}>
                  {s.label}
                </Typography>
                <Typography
                  component="div"
                  sx={{ fontFamily: t.monoFont, fontSize: 11, color: t.dim, mt: 0.25 }}
                >
                  {s.sub}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}

// ── 2. Caliber shortcuts (tested: region "Shop by caliber", 8 link chips) ────

function pickTopCalibersByDemand(inventoryFacets: FacetValue[] | undefined): string[] {
  if (Array.isArray(inventoryFacets) && inventoryFacets.length > 0) {
    return [...inventoryFacets]
      .sort((a, b) => b.count - a.count)
      .slice(0, TOP_CALIBER_COUNT)
      .map((f) => f.value);
  }
  return FALLBACK_CALIBERS.slice(0, TOP_CALIBER_COUNT);
}

function CaliberShortcutsSection() {
  const inventoryQuery = useQuery<FacetValue[]>({
    queryKey: ['calibers', 'inventory-counts'],
    queryFn: () => getCaliberInventoryCounts(),
    staleTime: 60 * 60 * 1000,
    retry: false,
  });

  const taxonomyQuery = useQuery<TaxonomyTerm[]>({
    queryKey: ['taxonomy', 'calibers'],
    queryFn: () => getTaxonomy('calibers'),
    staleTime: 60 * 60 * 1000,
    retry: false,
    enabled: inventoryQuery.isError || (!inventoryQuery.isLoading && !inventoryQuery.data?.length),
  });

  const isLoading = inventoryQuery.isLoading;

  let calibers: string[];
  if (Array.isArray(inventoryQuery.data) && inventoryQuery.data.length > 0) {
    calibers = pickTopCalibersByDemand(inventoryQuery.data);
  } else if (Array.isArray(taxonomyQuery.data) && taxonomyQuery.data.length > 0) {
    calibers = taxonomyQuery.data.slice(0, TOP_CALIBER_COUNT).map((term) => term.canonical);
  } else {
    calibers = FALLBACK_CALIBERS.slice(0, TOP_CALIBER_COUNT);
  }

  return (
    <Box component="section" aria-labelledby="calibers-heading">
      <Eyebrow component="div" sx={{ mb: 1 }}>
        Jump in
      </Eyebrow>
      <Typography id="calibers-heading" variant="h2" component="h2" sx={{ mb: 2 }}>
        Shop by caliber
      </Typography>
      {isLoading ? (
        <CaliberSkeletonRow />
      ) : (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {calibers.map((caliber) => (
            <Chip
              key={caliber}
              component={RouterLink}
              to={`/ammo/${caliberToSlug(caliber)}`}
              label={caliber}
              clickable
              variant="outlined"
              sx={{ fontWeight: 500 }}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
}

function CaliberSkeletonRow() {
  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap aria-hidden="true">
      {Array.from({ length: TOP_CALIBER_COUNT }).map((_, idx) => (
        <Skeleton key={idx} variant="rounded" width={96} height={32} />
      ))}
    </Stack>
  );
}

// ── 3. Best deals (tested: region "Best current deals") ──────────────────────

function BestDealsSection() {
  // #153/#106: one cacheable request for the whole cross-section — the single
  // cheapest in-stock listing per top caliber — instead of N parallel
  // per-caliber `searchListings` queries. The /search/deals response is
  // identical for every anonymous visitor, so a shared/browser cache serves it
  // and it barely touches the rate limit.
  const dealsQuery = useQuery<DealsResponse>({
    queryKey: ['deals'],
    queryFn: () => getDeals(),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const hits: SearchHit[] = dealsQuery.data?.deals ?? [];

  // Adapt to ListingRow's SearchResponse-shaped query prop. Only surface the
  // error branch when there's nothing to show.
  const listingQuery = {
    isLoading: dealsQuery.isLoading,
    isError: dealsQuery.isError && hits.length === 0,
    data: { results: hits } as SearchResponse,
  } as ReturnType<typeof useQuery<SearchResponse>>;

  return (
    <Box component="section" aria-labelledby="deals-heading">
      <SectionHeader
        eyebrow="● Hand-picked deals"
        eyebrowAccent
        title="Best current deals"
        subtitle="The cheapest in-stock listing in each of the top calibers — refreshed every scrape."
        headingId="deals-heading"
      />
      <ListingRow
        query={listingQuery}
        skeletonCount={DEALS_LIMIT}
        emptyMessage="No deals to show right now. Try a search instead."
      />
    </Box>
  );
}

// ── 4. Browse by caliber grid ────────────────────────────────────────────────

function BrowseByCaliberSection() {
  const theme = useTheme();
  const t = theme.palette.tactical;
  const inventoryQuery = useQuery<FacetValue[]>({
    queryKey: ['calibers', 'inventory-counts'],
    queryFn: () => getCaliberInventoryCounts(),
    staleTime: 60 * 60 * 1000,
    retry: false,
  });

  const items: FacetValue[] = (
    inventoryQuery.data && inventoryQuery.data.length > 0
      ? inventoryQuery.data
      : FALLBACK_CALIBERS.map((value) => ({ value, count: 0 }))
  ).slice(0, BROWSE_CALIBER_COUNT);

  return (
    <Box component="section" aria-labelledby="browse-heading">
      <SectionHeader
        eyebrow="Browse the market"
        title="Every caliber, ranked by inventory"
        headingId="browse-heading"
      />
      <Box
        sx={{
          display: 'grid',
          gap: 1.25,
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',
            sm: 'repeat(3, 1fr)',
            md: 'repeat(4, 1fr)',
            lg: 'repeat(6, 1fr)',
          },
        }}
      >
        {items.map((c) => (
          <Box
            key={c.value}
            component={RouterLink}
            to={`/ammo/${caliberToSlug(c.value)}`}
            sx={{
              textDecoration: 'none',
              bgcolor: t.panel,
              border: `1px solid ${t.line}`,
              borderRadius: 2,
              p: 1.75,
              minHeight: 116,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              transition: 'border-color 120ms ease',
              '&:hover': { borderColor: t.lineHi },
            }}
          >
            <Box>
              <Cartridge
                caliber={c.value}
                height={20}
                decorative
                style={{ alignItems: 'flex-start' }}
              />
              <Typography
                sx={{
                  mt: 1,
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  color: t.text,
                  letterSpacing: '-0.01em',
                }}
              >
                {c.value}
              </Typography>
            </Box>
            <Typography sx={{ fontFamily: t.monoFont, fontSize: 11, color: t.dim }}>
              {c.count > 0 ? `${c.count.toLocaleString()} in stock` : 'View listings'}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// ── 5. Back in stock ─────────────────────────────────────────────────────────

function BackInStockSection() {
  const backInStockQuery = useQuery<SearchResponse>({
    queryKey: ['search', 'back-in-stock'],
    queryFn: () =>
      searchListings({ sort: 'newest', inStockOnly: true, limit: BACK_IN_STOCK_LIMIT }),
    retry: false,
  });

  return (
    <Box component="section" aria-labelledby="back-in-stock-heading">
      <SectionHeader
        eyebrow="Fresh on the shelf"
        title="Back in stock"
        headingId="back-in-stock-heading"
      />
      <ListingRow
        query={backInStockQuery}
        skeletonCount={BACK_IN_STOCK_LIMIT}
        emptyMessage="Nothing has come back in stock recently."
      />
    </Box>
  );
}

// ── Shared section header + listing row ──────────────────────────────────────

function SectionHeader({
  eyebrow,
  title,
  subtitle,
  headingId,
  eyebrowAccent,
}: {
  eyebrow: string;
  title: string;
  subtitle?: string;
  headingId: string;
  eyebrowAccent?: boolean;
}) {
  const theme = useTheme();
  const t = theme.palette.tactical;
  return (
    <Box sx={{ mb: 2.5 }}>
      <Eyebrow component="div" color={eyebrowAccent ? t.accent : undefined} sx={{ mb: 1 }}>
        {eyebrow}
      </Eyebrow>
      <Typography id={headingId} variant="h2" component="h2">
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="body2" sx={{ color: t.dim, mt: 0.5 }}>
          {subtitle}
        </Typography>
      )}
    </Box>
  );
}

interface ListingRowProps {
  query: ReturnType<typeof useQuery<SearchResponse>>;
  skeletonCount: number;
  emptyMessage: string;
}

function ListingRow({ query, skeletonCount, emptyMessage }: ListingRowProps) {
  if (query.isLoading) {
    return <ListingSkeletonRow count={skeletonCount} />;
  }
  if (query.isError) {
    return (
      <Alert severity="info" role="status">
        Listings are loading slowly right now. Please try a search.
      </Alert>
    );
  }
  const hits: SearchHit[] = query.data?.results ?? [];
  if (hits.length === 0) {
    return <Alert severity="info">{emptyMessage}</Alert>;
  }
  return (
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, 1fr)',
          md: 'repeat(3, 1fr)',
          lg: 'repeat(4, 1fr)',
        },
      }}
    >
      {hits.map((hit) => (
        <ListingCard key={hit.listingId} hit={hit} variant="compact" />
      ))}
    </Box>
  );
}

function ListingSkeletonRow({ count }: { count: number }) {
  return (
    <Box
      aria-hidden="true"
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, 1fr)',
          md: 'repeat(3, 1fr)',
          lg: 'repeat(4, 1fr)',
        },
      }}
    >
      {Array.from({ length: count }).map((_, idx) => (
        <Skeleton key={idx} variant="rounded" height={232} />
      ))}
    </Box>
  );
}

// ── 6. Price-drop CTA ────────────────────────────────────────────────────────

function PriceDropCtaSection() {
  const theme = useTheme();
  const t = theme.palette.tactical;
  return (
    <Box component="section" aria-labelledby="watch-cta-heading">
      <Box
        sx={{
          background: `linear-gradient(135deg, ${t.panel} 0%, ${t.bg} 100%)`,
          border: `1px solid ${t.line}`,
          borderRadius: 3,
          p: { xs: 3, md: 6 },
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1.3fr 1fr' },
          gap: { xs: 3, md: 6 },
          alignItems: 'center',
        }}
      >
        <Box>
          <Eyebrow component="div" color={t.accent} sx={{ mb: 1.5 }}>
            ● Price-drop alerts
          </Eyebrow>
          <Typography
            id="watch-cta-heading"
            variant="h2"
            component="h2"
            sx={{ fontSize: { xs: '1.9rem', md: '2.6rem' }, lineHeight: 1.05 }}
          >
            Set the price. We&apos;ll wait for it.
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: t.dim, mt: 2.5, maxWidth: 540, lineHeight: 1.55 }}
          >
            Save any search to your watchlist. Round Recon re-checks every retailer and fires an
            alert the moment your trigger conditions hit — with deduplication and quiet hours
            respected.
          </Typography>
          <Stack direction="row" spacing={1.5} sx={{ mt: 3.5 }} flexWrap="wrap" useFlexGap>
            <Button
              component={RouterLink}
              to="/register"
              variant="contained"
              color="primary"
              size="large"
            >
              Create your first watch
            </Button>
            <Button component={RouterLink} to="/search" variant="outlined" size="large">
              Browse listings
            </Button>
          </Stack>
        </Box>

        <Box sx={{ bgcolor: t.bgInset, border: `1px solid ${t.line}`, borderRadius: 2, p: 2.5 }}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ fontFamily: t.monoFont, fontSize: 12, mb: 2 }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: t.green,
                boxShadow: `0 0 8px ${t.green}`,
              }}
            />
            <Box component="span" sx={{ color: t.green, letterSpacing: '0.08em' }}>
              SAMPLE ALERT
            </Box>
          </Stack>
          <Typography sx={{ fontWeight: 600, fontSize: '1.05rem' }}>
            Federal Champion 9mm 115gr FMJ
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25, mt: 2 }}>
            {[
              ['TARGET', '≤ $0.30/rd', t.text],
              ['TRIGGER', 'price drop', t.text],
              ['CHANNELS', 'email · sms', t.text],
              ['STATUS', 'armed', t.green],
            ].map(([label, value, color]) => (
              <Box
                key={label}
                sx={{ bgcolor: t.panel, borderRadius: 1.5, p: 1.25, fontFamily: t.monoFont }}
              >
                <Box sx={{ color: t.dim, fontSize: 10, letterSpacing: '0.1em' }}>{label}</Box>
                <Box sx={{ color, fontSize: 13, mt: 0.5 }}>{value}</Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// ── 7. Privacy notice ────────────────────────────────────────────────────────

function PrivacyNoticeSection() {
  return (
    <Box component="section" aria-labelledby="privacy-heading">
      <Alert severity="info" icon={false} sx={{ alignItems: 'flex-start' }}>
        <Typography id="privacy-heading" variant="h2" component="h2" sx={{ fontSize: '1.125rem' }}>
          Your privacy, your controls
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          Round Recon lets you decide what data is used for analytics, personalization, behavioral
          advertising, and advertiser data sharing — each is a separate, revocable consent.{' '}
          <MuiLink component={RouterLink} to="/privacy">
            Read the full privacy notice
          </MuiLink>
          .
        </Typography>
      </Alert>
    </Box>
  );
}

// ── 8. Retailer / advertiser CTA ─────────────────────────────────────────────

function AdvertiserCtaSection() {
  const theme = useTheme();
  const t = theme.palette.tactical;
  return (
    <Box component="section" aria-labelledby="advertiser-heading">
      <Box
        sx={{
          bgcolor: t.panel,
          border: `1px solid ${t.line}`,
          borderRadius: 3,
          p: { xs: 3, md: 4 },
        }}
      >
        <Stack spacing={1.5}>
          <Typography id="advertiser-heading" variant="h2" component="h2">
            Are you a retailer?
          </Typography>
          <Typography variant="body2" sx={{ color: t.dim, maxWidth: 640 }}>
            Round Recon sends ready-to-buy shoppers to your store. Learn how sponsored placements,
            brand pages, and aggregated reporting work — without selling your buyers&apos; data.
          </Typography>
          <Box>
            <Button component={RouterLink} to="/register-advertiser" variant="outlined">
              Advertise on Round Recon
            </Button>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
}
